import {
  useCanCall,
  useConsumableReplenishments,
  useEventHistory,
  useHistory,
  useLogs,
  useUsers,
  useWasteReplacements,
} from '@ctcells/api'
import { dataSourceFor } from '@ctcells/core'
import { errorMessage, t } from '@ctcells/i18n'
import { useMemo } from 'react'

import { canView } from '../access.ts'
import { useScreenAccess } from '../screenAccess.ts'

import type { AdminTabId } from '../admin/adminTabs.ts'

import { LogsScreen } from './LogsScreen.tsx'

/**
 * 로그(이벤트 · 시스템 · 감사) 컨테이너.
 *
 * 분류: **1. 실제로 연결** — 실물이 가장 두껍게 붙는 화면이다.
 * `GET /logs` 308,852건 · `GET /history` 3,578건(2026-09-17 실측).
 *
 * | 탭      | 경로                              |
 * | ------- | --------------------------------- |
 * | 이벤트  | `GET /history?event_type=event` + `…=batch_record` (`useEventHistory` — 한 목록) |
 * | 시스템  | `GET /logs`                       |
 * | 감사    | `GET /history?event_type=audit`   |
 * | 보충 기록 | `GET /consumable-replenishments` (`IFC-RO-32` · operator · admin) |
 * | 폐기통 교체 | `GET /waste-replacements` (`IFC-RO-20` · operator · admin) |
 *
 * 🔴 **기록 두 탭의 사람 칸은 user_id(UUID)** 다(dev2 2026-10-12 실측). 이름은 `GET /users` 대조로 얻는데 그 경로가 **admin 한정**
 *    (`listUsers`)이라 operator 는 부르지 않고(`skip`) UUID 그대로 본다 — 403 이 날 요청을 만들지 않는다.
 *
 * 🔴 **이벤트 탭은 원장 둘이다**(2026-10-11) — v0.9.4 서버가 배치 기록(발송 · 전이 · 멈춤 사유 …)을 `event_log` 에서
 *    `batch_record` 로 뗐다. 하나만 읽으면 새 작업의 기록이 이 탭에서 사라진다. 「구분」 열이 원장을 말한다.
 *
 * 🔴 **시스템·감사 탭은 `settings`·`maintenance` 권한이 있어야 보인다.** 그 판정은 화면(`Tabs`)이
 *    이미 한다 — 컨테이너는 **세 축을 다 받아 둔다.** 권한이 없으면 탭이 안 그려질 뿐이다.
 *    ⚠ 그래서 권한 없는 역할도 `/logs`·감사 이력을 **요청은 한다.** 서버가 403 이어도
 *      화면 전체가 에러로 떨어지지 않고 **탭별로 갈라 흘린다**(아래 `notices`).
 *
 * 🔴 **이 화면의 세 경로는 전부 operator/admin 이다**(2026-09-21 실측) —
 *    `GET /logs`(`views/logs.py` `ALLOWED_ROLES = {admin, operator}`) ·
 *    `GET /history` ×2(`views/history.py` `PRIVILEGED_ROLES`). PII 접촉면이라 그렇다
 *    (정본 `07 §2.4`). 그래서 관리 탭 줄에서 **로그 탭의 권한이 `view`** 다 — viewer 에게는
 *    숨기지 않고 **비활성 + 사유**로 선다.
 *
 * ⚠ **정렬 파라미터가 없다**(실측). 30만 건 위에서 「최근 N건」을 서버에 요청할 방법이 없고,
 *   무엇이 먼저 오는지는 서버 기본 정렬에 달렸다. 화면의 열 정렬은 **받아 온 페이지 안에서만**
 *   된다. → 보고 대상: `GET /logs`·`/history` 에 `sort`(최소한 `ts desc` 보장)가 필요하다.
 *
 * ⚠ 기간·모듈·심각도 필터 칩은 아직 **서버로 안 넘긴다.** `/logs` 는 `level`·`source`·`from`·`to`,
 *   `/history` 는 `event_type`·`from`·`to` 를 받지만 화면 칩이 값을 들고 있지 않다(무수정 원칙).
 *   칩에 상태가 붙는 순간 그대로 인자로 흐른다.
 *
 * 🔴 **주기 폴링 없다.** 스냅샷 1회 + 구독 무효화가 전부다(DP-01).
 *
 * 경계 다섯
 * | 로딩 | 화면의 `loading` 으로 넘긴다(표가 스켈레톤을 그린다)           |
 * | 에러 | **실패한 탭의 표 안에만** 사유. 코드는 노출하지 않는다          |
 * | 빈   | 0행이면 표가 「기록 없음」을 그린다                             |
 * | 권한 | 탭 노출은 화면이, 버튼 비활성은 `gate()` 가 한다               |
 * | 끊김 | `access.controlDisabled` → 화면 상단 「연결 끊김」 띠          |
 */

/**
 * 한 번에 받는 상한. 서버 최대는 500 이지만 30만 건 앞에서 숫자를 키워 봐야 의미가 없고
 * (정렬을 못 정한다) 표 렌더 비용만 커진다. 🔴 숫자는 여기 한 곳이다.
 */
const PAGE_LIMIT = 200

/**
 * @param adminTab 열린 무리 탭. 🔴 **라우트가 넘긴다** — 탭이 곧 주소다.
 *   **안 넘기면 무리 탭 줄을 안 그린다**(`useGroupTabLayout` 주석). 장비 관점 콘솔이 그렇게 쓴다.
 */
export interface LogsContainerProps {
  adminTab?: AdminTabId | undefined
}

export function LogsContainer({ adminTab }: LogsContainerProps = {}) {
  return dataSourceFor('logs') === 'demo' ? (
    <LogsDemo adminTab={adminTab} />
  ) : (
    <LogsLive adminTab={adminTab} />
  )
}

/** demo 경로는 props 를 안 넘긴다 = 화면 기본값(이벤트 원장 둘 · 감사 · 앱 로그 목)이 그대로 산다 */
function LogsDemo({ adminTab }: LogsContainerProps) {
  const access = useScreenAccess()
  return <LogsScreen access={access} {...(adminTab === undefined ? {} : { adminTab })} />
}

function LogsLive({ adminTab }: LogsContainerProps) {
  const access = useScreenAccess()
  const events = useEventHistory({ limit: PAGE_LIMIT })
  const audits = useHistory({ limit: PAGE_LIMIT, event_type: 'audit' })
  const logs = useLogs({ limit: PAGE_LIMIT })
  /*
   * 기록 두 경로는 operator · admin(`HISTORY_READ_ROLES`) — 그 둘에게만 있는 `view` 권한이 없으면 부르지 않는다
   * (viewer 403 을 만들지 않는다 · 이 탭 문도 `view` 라 viewer 는 원래 못 온다).
   */
  const canReadRecords = canView(access)
  const noRecordAccess = t('reason.noPermission', { perm: 'view' })
  const replenishments = useConsumableReplenishments({ limit: PAGE_LIMIT, skip: !canReadRecords })
  const wasteReplacements = useWasteReplacements({ limit: PAGE_LIMIT, skip: !canReadRecords })
  // 사람 칸 이름 대조 — admin 만(`listUsers`). 사용자 화면과 같은 키라 캐시를 나눠 쓴다
  const canListUsers = useCanCall('listUsers')
  const users = useUsers({ limit: PAGE_LIMIT, skip: !canListUsers })
  const userNames = useMemo(
    () =>
      users.data === undefined
        ? undefined
        : new Map(
            users.data.items.flatMap((u) =>
              u.user_id == null ? [] : [[u.user_id, u.username] as const],
            ),
          ),
    [users.data],
  )

  /*
   * 🔴 **세 요청이 «각각» 제 탭에만 영향을 준다**(2026-09-21 정정).
   *
   * ⚠ 종전에는 `[events, audits, logs].find(isError)` 하나로 **화면 전체**를 사유로 바꿨다.
   *   세 경로가 전부 operator/admin 이라(`views/logs.py` `ALLOWED_ROLES` ·
   *   `views/history.py` `PRIVILEGED_ROLES`) viewer 는 셋 다 403 인데, 그때 화면을 통째로
   *   갈아 끼우면 **관리 탭 줄이 같이 사라져** viewer 가 모듈 탭(서버가 200 을 주는 화면)으로
   *   갈 길을 잃는다. ⇒ 틀은 남기고 **탭마다 제 사유**를 적는다.
   * ⚠ 옛 주석이 「탭별로 갈라 흘린다」고 적어 놓고 코드는 반대였다 — 이제 코드가 그 말을 지킨다.
   *
   * 🔴 **부분 성공을 「일부만 보이는 로그」로 오해하게 두지 않는다.** 못 본 탭은 빈 표가 아니라
   *    **사유**를 이고 있으므로, 사용자는 어느 탭이 비어 있는 «이유»를 그 자리에서 본다.
   */
  const noticeOf = (query: { isError: boolean; error?: unknown }): string | undefined =>
    query.isError ? errorMessage(query.error as Parameters<typeof errorMessage>[0]) : undefined

  return (
    <LogsScreen
      {...(adminTab === undefined ? {} : { adminTab })}
      access={access}
      audits={audits.data?.items ?? []}
      events={events.data?.items ?? []}
      loading={
        events.isPending ||
        audits.isPending ||
        logs.isPending ||
        (canReadRecords && (replenishments.isPending || wasteReplacements.isPending))
      }
      replenishments={replenishments.data?.items ?? []}
      wasteReplacements={wasteReplacements.data?.items ?? []}
      {...(userNames === undefined ? {} : { userNames })}
      logs={logs.data?.items ?? []}
      notices={{
        events: noticeOf(events),
        logs: noticeOf(logs),
        audits: noticeOf(audits),
        // 안 부른 것은 「0건」이 아니라 권한 사유다
        replenishments: canReadRecords ? noticeOf(replenishments) : noRecordAccess,
        wasteReplacements: canReadRecords ? noticeOf(wasteReplacements) : noRecordAccess,
      }}
    />
  )
}
